/* eslint-disable import/no-duplicates */
import { ReactElement, ReactNode } from 'react'
import { NextPage } from 'next'
import type { AppContext, AppProps } from 'next/app'
import { default as NextApp } from 'next/app'
import dynamic from 'next/dynamic'

import Loader from '@/components/base/Loader'
import SVGSprites from '@/components/base/SVGSprites'
import Footer from '@/components/layout/Footer'
import Header from '@/components/layout/Header'
import Landing from '@/components/layout/Landing'
import { MixerProvider } from '@/contexts/mixer'
import { Store, StoreProvider } from '@/contexts/store'
import { ThemeProvider } from '@/contexts/theme'
import { TimelineProvider } from '@/contexts/timeline'
import { TransitionProvider } from '@/contexts/transition'

import '@/styles/globals.css'

import '@/base/gsap'

const GridHelper = dynamic(() => import('@/components/base/GridHelper'), {
  ssr: false
})

const Stage = dynamic(() => import('@/webgl/Stage'), {
  ssr: false
})

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  // eslint-disable-next-line no-unused-vars
  getLayout?: (page: ReactElement, props: AppProps) => ReactNode
}

export default function App({
  Component,
  pageProps,
  menu,
  strings
}: AppProps & Store & { Component: NextPageWithLayout }) {
  const getLayout = Component.getLayout ?? ((page) => page)

  return (
    <StoreProvider home={pageProps.homeData || pageProps} menu={menu} strings={strings}>
      <ThemeProvider fluid>
        <MixerProvider>
          <TimelineProvider>
            {process.env.NODE_ENV !== 'production' ? <GridHelper /> : null}
            <Stage />
            <Loader />
            <Header />
            <Landing />
            <TransitionProvider>
              {getLayout(<Component {...pageProps} />, pageProps)}
            </TransitionProvider>
            <SVGSprites />
            <Footer />
          </TimelineProvider>
        </MixerProvider>
      </ThemeProvider>
    </StoreProvider>
  )
}

App.getInitialProps = async (appContext: AppContext) => {
  const appProps = await NextApp.getInitialProps(appContext)

  const menuData = require(`@/data/menu-locations.json`)
  const stringsData = require(`@/data/options.json`)

  return {
    ...appProps,
    strings: stringsData || {},
    menu: Object.keys(menuData).length > 0 ? menuData : {}
  }
}
